import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Loader2, Sparkles, ExternalLink, AlertTriangle, Check, Phone, ArrowRight,
} from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { api, type LlamadaDetalle, type ResultadoProcesarLead } from '@/lib/api';
import { ZONE_LABELS } from '@/lib/utils';

/**
 * Volcar una llamada sobre la ficha de un lead de propietario.
 *
 * Tres pasos: elegir a quién, esperar al modelo y leer lo que ha sacado. El
 * paso de elegir no se puede saltar aunque el teléfono cuadre con un lead —solo
 * se preselecciona—, porque vincular la llamada equivocada mete conclusiones
 * falsas en la ficha de otra persona, y eso no se ve hasta que alguien llama
 * citando algo que nunca se dijo.
 *
 * Lo que devuelve el modelo se aplica solo sobre los campos vacíos de la ficha;
 * de eso se encarga el servidor. Aquí la única acción que queda por decidir es
 * la fase del pipeline, que se sugiere y se aplica de un clic: mover un lead de
 * fase es una decisión comercial, no una conclusión de una transcripción.
 */

/** Cómo se llama cada campo de la ficha cuando hay que decir que se ha rellenado. */
export const CAMPOS: Record<string, string> = {
  email: 'Correo',
  propertyAddress: 'Dirección',
  propertyZone: 'Zona',
  propertyType: 'Tipo',
  propertyBedrooms: 'Dormitorios',
  propertyCurrentState: 'Estado actual',
  sourceDetail: 'Origen',
};

export const ESTADOS: Record<string, string> = {
  empty: 'Vacía',
  owner_use: 'Uso del propietario',
  long_term_rental: 'Alquiler largo plazo',
  already_tourist_rental: 'Ya alquiler turístico',
};

export const TEMPERATURA: Record<string, { texto: string; clase: string }> = {
  fria: { texto: 'Fría', clase: 'text-sky-700 dark:text-sky-300' },
  templada: { texto: 'Templada', clase: 'text-amber-700 dark:text-amber-300' },
  caliente: { texto: 'Caliente', clase: 'text-rose-700 dark:text-rose-300' },
};

/** El valor tal y como se lee, no como se guarda: `atlanterra` no dice nada. */
export function comoSeLee(campo: string, valor: unknown): string {
  const texto = String(valor);
  if (campo === 'propertyZone') return ZONE_LABELS[texto] || texto;
  if (campo === 'propertyCurrentState') return ESTADOS[texto] || texto;
  return texto;
}

function plano(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Los nueve últimos dígitos: es lo que hace iguales a «+34 609 76 02 53» y «609760253». */
function clave(telefono: string | null | undefined): string {
  const digitos = String(telefono ?? '').replace(/\D/g, '');
  return digitos.length >= 9 ? digitos.slice(-9) : '';
}

interface LeadMinimo {
  id: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  propertyZone?: string;
  convertedToOwnerId?: string;
}

export default function ProcesarLead({
  llamada,
  onCerrar,
  onProcesada,
}: {
  llamada: LlamadaDetalle;
  onCerrar: () => void;
  /** Para que la llamada de la lista refleje que ya está procesada. */
  onProcesada?: (resultado: ResultadoProcesarLead) => void;
}) {
  const [leads, setLeads] = useState<LeadMinimo[] | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [elegido, setElegido] = useState<string | null>(null);
  const [procesando, setProcesando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoProcesarLead | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [faseAplicada, setFaseAplicada] = useState(false);
  const [aplicandoFase, setAplicandoFase] = useState(false);

  useEffect(() => {
    let cancelado = false;
    api
      .getLeads()
      .then((todos: LeadMinimo[]) => {
        if (cancelado) return;
        // Un lead ya convertido en propietario tiene su ficha viva en
        // Propietarios; escribirle aquí dejaría la conclusión en la ficha muerta.
        const vivos = todos.filter((l) => !l.convertedToOwnerId);
        setLeads(vivos);
        const mismoTelefono = clave(llamada.phone);
        const encontrado = mismoTelefono && vivos.find((l) => clave(l.phone) === mismoTelefono);
        if (encontrado) setElegido(encontrado.id);
      })
      .catch((e) => { if (!cancelado) setError(e instanceof Error ? e.message : 'Error cargando los leads'); });
    return () => { cancelado = true; };
  }, [llamada.phone]);

  const preseleccionado = useMemo(() => {
    const t = clave(llamada.phone);
    return Boolean(t && leads?.some((l) => clave(l.phone) === t));
  }, [leads, llamada.phone]);

  const visibles = useMemo(() => {
    if (!leads) return [];
    const q = plano(busqueda.trim());
    const digitos = busqueda.replace(/\D/g, '');
    if (!q) return leads.slice(0, 50);
    return leads
      .filter((l) => {
        const nombre = plano(`${l.firstName || ''} ${l.lastName || ''}`);
        if (nombre.includes(q)) return true;
        if (l.email && plano(l.email).includes(q)) return true;
        if (digitos.length >= 3 && (l.phone || '').replace(/\D/g, '').includes(digitos)) return true;
        return false;
      })
      .slice(0, 50);
  }, [leads, busqueda]);

  const procesar = async () => {
    if (!elegido || procesando) return;
    setProcesando(true);
    setError(null);
    try {
      const r = await api.procesarLlamadaParaLead(llamada.id, elegido);
      setResultado(r);
      onProcesada?.(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo procesar la llamada');
    } finally {
      setProcesando(false);
    }
  };

  const aplicarFase = async () => {
    const fase = resultado?.lectura.faseSugerida;
    if (!fase || !resultado || aplicandoFase) return;
    setAplicandoFase(true);
    try {
      await api.updateLead(resultado.enlace.leadId, { pipelineStageId: fase.id });
      setFaseAplicada(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar la fase');
    } finally {
      setAplicandoFase(false);
    }
  };

  const nombreFase = (id: string) => resultado?.fases.find((f) => f.id === id)?.nombre || id;

  return (
    <Modal isOpen onClose={onCerrar} title="Procesar para Lead" size="lg">
      {resultado ? (
        <Resultado
          resultado={resultado}
          nombreFase={nombreFase}
          faseAplicada={faseAplicada}
          aplicandoFase={aplicandoFase}
          onAplicarFase={aplicarFase}
          error={error}
        />
      ) : procesando ? (
        <div className="py-12 text-center space-y-3">
          <Loader2 className="animate-spin text-slate-400 mx-auto" size={28} />
          <p className="text-sm text-slate-600 dark:text-slate-300">Leyendo la conversación…</p>
          <p className="text-xs text-slate-400">
            Una llamada larga puede tardar un minuto. No cierres esta ventana.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {llamada.lead && (
            <p className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>
                Esta llamada ya se procesó para <strong>{llamada.lead.leadNombre}</strong>. Volver a
                hacerlo añade otra actividad; los campos que ya se rellenaron no cambian.
              </span>
            </p>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <input
              className="input pl-9"
              placeholder="Buscar un lead por nombre, teléfono o correo…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>

          {preseleccionado && !busqueda && (
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <Phone size={12} className="text-slate-400" />
              Preseleccionado por coincidir el teléfono de la llamada. Compruébalo antes de seguir.
            </p>
          )}

          {!leads ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" size={22} /></div>
          ) : !visibles.length ? (
            <p className="py-8 text-sm text-slate-500 text-center">
              {busqueda ? 'Ningún lead coincide con la búsqueda.' : 'No hay leads de propietarios.'}
            </p>
          ) : (
            <ul className="max-h-[320px] overflow-y-auto divide-y divide-slate-100 dark:divide-white/5 border border-slate-100 dark:border-white/5 rounded-lg">
              {visibles.map((l) => {
                const nombre = `${l.firstName || ''} ${l.lastName || ''}`.trim() || 'Sin nombre';
                return (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => setElegido(l.id)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors ${
                        elegido === l.id
                          ? 'bg-sereno-dark/5 dark:bg-white/10'
                          : 'hover:bg-slate-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-slate-800 dark:text-slate-100 truncate">{nombre}</span>
                        <span className="block text-xs text-slate-400 truncate">
                          {l.phone || 'sin teléfono'}
                          {l.propertyZone && ` · ${ZONE_LABELS[l.propertyZone] || l.propertyZone}`}
                        </span>
                      </span>
                      {elegido === l.id && <Check size={15} className="text-sereno-dark dark:text-white shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" className="btn-ghost" onClick={onCerrar}>Cancelar</button>
            <button
              type="button"
              className="btn-primary flex items-center gap-1.5 disabled:opacity-40"
              disabled={!elegido}
              onClick={procesar}
            >
              <Sparkles size={15} />
              Procesar
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Resultado({
  resultado, nombreFase, faseAplicada, aplicandoFase, onAplicarFase, error,
}: {
  resultado: ResultadoProcesarLead;
  nombreFase: (id: string) => string;
  faseAplicada: boolean;
  aplicandoFase: boolean;
  onAplicarFase: () => void;
  error: string | null;
}) {
  const { lectura, rellenados, enlace } = resultado;
  const temp = lectura.temperatura ? TEMPERATURA[lectura.temperatura] : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Volcado en la ficha de <strong>{enlace.leadNombre}</strong>
        </p>
        <Link to={`/leads/propietarios?id=${encodeURIComponent(enlace.leadId)}`} className="btn-ghost text-sm flex items-center gap-1.5">
          Abrir la ficha <ExternalLink size={13} />
        </Link>
      </div>

      <div className="card p-3 space-y-3 bg-slate-50 dark:bg-white/5">
        <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">
          {lectura.resumen || 'El modelo no ha sacado un resumen de esta llamada.'}
        </p>
        {temp && <p className={`text-sm font-medium ${temp.clase}`}>Temperatura: {temp.texto}</p>}
      </div>

      {lectura.puntosClave.length > 0 && (
        <Lista titulo="Puntos clave" items={lectura.puntosClave} />
      )}
      {lectura.objeciones.length > 0 && (
        <Lista titulo="Objeciones" items={lectura.objeciones} />
      )}

      {lectura.siguientePaso && (
        <div>
          <p className="label mb-1">Siguiente paso</p>
          <p className="text-sm text-slate-700 dark:text-slate-200">{lectura.siguientePaso}</p>
        </div>
      )}

      <div>
        <p className="label mb-1.5">Ficha</p>
        {rellenados.length ? (
          <ul className="space-y-1">
            {rellenados.map((c) => (
              <li key={c} className="text-sm text-slate-700 dark:text-slate-200 flex items-baseline gap-2">
                <Check size={13} className="text-emerald-600 shrink-0 translate-y-0.5" />
                <span><span className="text-slate-400">{CAMPOS[c] || c}:</span> {comoSeLee(c, lectura.campos[c])}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">
            No se ha rellenado ningún campo: o la llamada no aportaba datos nuevos, o los que
            aportaba ya estaban escritos. Lo escrito a mano no se toca.
          </p>
        )}
      </div>

      {lectura.faseSugerida && (
        <div className="card p-3 flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm text-slate-700 dark:text-slate-200">
              Fase sugerida: <strong>{nombreFase(lectura.faseSugerida.id)}</strong>
            </p>
            {lectura.faseSugerida.porQue && (
              <p className="text-xs text-slate-500 mt-0.5">{lectura.faseSugerida.porQue}</p>
            )}
          </div>
          {faseAplicada ? (
            <span className="text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
              <Check size={14} /> Aplicada
            </span>
          ) : (
            <button
              type="button"
              className="btn-ghost text-sm flex items-center gap-1.5 disabled:opacity-40"
              disabled={aplicandoFase}
              onClick={onAplicarFase}
            >
              {aplicandoFase ? <Loader2 size={13} className="animate-spin" /> : <ArrowRight size={13} />}
              Mover a esta fase
            </button>
          )}
        </div>
      )}

      {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}

      <p className="text-xs text-slate-400">
        Leído por {lectura.modelo} sobre la transcripción automática, que puede contener errores.
        Queda registrado como actividad en la ficha del lead.
      </p>
    </div>
  );
}

function Lista({ titulo, items }: { titulo: string; items: string[] }) {
  return (
    <div>
      <p className="label mb-1.5">{titulo}</p>
      <ul className="space-y-1">
        {items.map((t, i) => (
          <li key={i} className="text-sm text-slate-700 dark:text-slate-200 flex items-baseline gap-2">
            <span className="text-slate-300 shrink-0">•</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
